import type { Log } from "../log/Log";
import type { SkeletonData, TextureAtlas } from "@esotericsoftware/spine-pixi-v8";
import { createSkeletonData } from "./createSkeletonData";

/**
 * Parses each skeleton ONCE per key and hands the shared data to
 * every `Spine` instance built from it. Keys are the registry aliases.
 */
export class SkeletonDataCache {
  private readonly cache = new Map<string, SkeletonData>();

  public constructor(
    private readonly resolve: (path: string) => { skeleton: unknown; atlas: TextureAtlas } | null,
    private readonly log: Log = () => {},
  ) {}

  public get(path: string, scale = 1): SkeletonData | null {
    const key = `${path}@${scale}`;
    const cached = this.cache.get(key);
    if (cached) return cached;
    const source = this.resolve(path);
    if (!source) return null;
    try {
      const data = createSkeletonData({ skeleton: source.skeleton, atlas: source.atlas, scale });
      this.cache.set(key, data);
      return data;
    } catch (error) {
      this.log(`[SkeletonDataCache] "${path}" failed to parse: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }

  public getOrThrow(path: string, scale = 1): SkeletonData {
    const data = this.get(path, scale);
    if (!data) throw new Error(`SkeletonDataCache: skeleton "${path}" is not loaded`);
    return data;
  }
}
